import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { IoAlertCircleOutline } from 'react-icons/io5';
import { useQuery } from 'react-query';
import { Alert, Card, Col, Flex, Row, Skeleton, Typography } from 'antd';
import { Asset } from 'shared-types';
import styled from 'styled-components';

import Box from 'components/Box/Box';

import useAssetsStore from 'hooks/stores/useAssetsStore';
import useProductsStore from 'hooks/stores/useProductsStore';
import useSessionStore from 'hooks/stores/useSessionStore';
import useAutomateUserWorkflow from 'hooks/useAutomateUserWorkflow';
import useRealTimeSidePreviewUpdates from 'hooks/useRealTimeUpdateSidePreview';

import API from 'services/API';

import AssetsAndSidesActions, { AssetsActionsVariant } from './AssetsAndSidesActions';
//import AssetsPreviews from './AssetsPeviews';
import Instructions from './Instructions';
import RealTimeAssetsProcessing from './RealTimeAssetsProcessing';

const SidebarCard = styled(Card)`
  max-height: 100vh;

  @media (max-width: 480px) {
    .ant-card-body {
      padding: 12px 8px !important;
    }
  }
`;

type Props = {
  showInstructions?: boolean;
  showAddBlankPage?: boolean;
  shouldFetchAssets?: boolean;
  autoScrollDependency?: any;
  // Optionele externe ref naar de daadwerkelijke scroll-container (de <Row>). Wordt gebruikt
  // door de virtualizer in PagesList.tsx, die buiten deze component moet weten welk DOM-element
  // scrolt. Zonder meegegeven ref valt terug op een interne ref (bestaand gedrag).
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  // 'sidebar' (default): de smalle kolom met SidesList uit de book-layout.
  // 'toolbar': compacte balk over de volle breedte (booklayout-view), zonder scroll-Row.
  variant?: AssetsActionsVariant;
  children?: React.ReactNode;
};

const Assets: FC<Props> = ({
  showInstructions = true,
  showAddBlankPage = false,
  shouldFetchAssets = true,
  autoScrollDependency,
  scrollContainerRef,
  variant = 'sidebar',
  children,
}) => {
  const sessionId = useSessionStore((state) => state.sessionId);
  const session = useSessionStore((state) => state.session);
  const { products, getSelectedProduct } = useProductsStore((state) => ({
    products: state.products,
    getSelectedProduct: state.getSelectedProduct,
  }));
  const setAssets = useAssetsStore((state) => state.setAssets);
  const internalRowRef = useRef<HTMLDivElement>(null);
  const rowRef = scrollContainerRef ?? internalRowRef;
  const { t } = useTranslation();

  // Get UI sideList configuration from session options (default = visible)
  const shouldShowQualityLabels = session?.options?.ui?.sideList?.showQualityLabels !== false;

  // Calculate page count difference for the current product
  const getPageCountInfo = () => {
    if (!session) return null;

    const currentProduct = getSelectedProduct();
    if (!currentProduct) return null;

    // All sides now have assets since empty sides are no longer created
    const actualSidesCount = currentProduct.sides?.length || 0;
    const expectedSidesCount = session.sidesCount;
    const difference = expectedSidesCount - actualSidesCount;
    if (difference === 0) return null;

    return {
      difference,
      message:
        difference > 0
          ? t('QualityCheck.AddPages', { count: difference })
          : t('QualityCheck.RemovePages', { count: Math.abs(difference) }),
    };
  };

  // Gebruik de nieuwe real-time assets preview updates hook
  const { error: previewError } = useRealTimeSidePreviewUpdates();

  const {
    data: assets,
    isLoading: isLoadingAssets,
    isRefetching,
    isError,
    error,
  } = useQuery<Asset[]>('assets', () => API.getAssets(sessionId), {
    enabled: !!sessionId && shouldFetchAssets,
    onSuccess: (data) => {
      setAssets(data);
    },
  });

  const { automateUserWorkflowInSingleSide, shouldSkipAutomateWorkflow } = useAutomateUserWorkflow(
    getSelectedProduct()?.id,
  );

  useEffect(() => {
    if (shouldSkipAutomateWorkflow) return;
    automateUserWorkflowInSingleSide();
  }, [automateUserWorkflowInSingleSide, shouldSkipAutomateWorkflow]);

  // auto scroll to bottom
  useEffect(
    function autoScrollToBottom() {
      if (rowRef.current) {
        rowRef.current.scrollTo({
          top: rowRef.current.scrollHeight + rowRef.current.clientHeight,
          behavior: 'smooth',
        });
      }
    },
    [autoScrollDependency],
  );

  if (isError || error) return <Box>{t('ErrorFetchingAssets')}</Box>;
  if (isLoadingAssets) return <Skeleton active />;

  if (variant === 'toolbar') {
    const pageCountInfo = getPageCountInfo();
    const warningsCount = shouldShowQualityLabels
      ? ((getSelectedProduct() as any)?.sidesWithWarnings?.length as number | undefined) || 0
      : 0;
    const renderInlineBadge = (message: string): JSX.Element => (
      <Flex align="center" gap={4}>
        <IoAlertCircleOutline style={{ fontSize: '1rem', color: '#cf1322' }} />
        <Typography.Text style={{ color: '#cf1322', fontSize: '0.85rem' }}>
          {message}
        </Typography.Text>
      </Flex>
    );

    return (
      <Card className="assets-toolbar" styles={{ body: { padding: '8px 12px' } }}>
        {previewError && <Alert message={previewError} type="error" showIcon closable />}
        <AssetsAndSidesActions showAddBlankPage={showAddBlankPage} variant="toolbar" />
        {(pageCountInfo || warningsCount > 0) && (
          <Flex gap={16} wrap="wrap" style={{ marginTop: 6 }}>
            {pageCountInfo && renderInlineBadge(pageCountInfo.message)}
            {warningsCount > 0 &&
              renderInlineBadge(t('PagesWithWarnings', { count: warningsCount }))}
          </Flex>
        )}
        <RealTimeAssetsProcessing />
      </Card>
    );
  }

  return (
    <>
      <SidebarCard className="assets-sidebar">
        {previewError && <Alert message={previewError} type="error" showIcon closable />}
        <RealTimeAssetsProcessing />
        <Box mt={1} className="sidebar-box">
          <AssetsAndSidesActions showAddBlankPage={showAddBlankPage} />
        </Box>
        {/* Page count badge */}
        {getPageCountInfo() && (
          <Flex
            justify="center"
            align="center"
            style={{
              backgroundColor: '#fff2f0',
              borderRadius: '6px',
              padding: '8px 12px',
              border: '1px solid #ffccc7',
              margin: '8px 0',
            }}
          >
            <IoAlertCircleOutline
              style={{
                fontSize: '1rem',
                marginRight: '6px',
                color: '#cf1322',
              }}
            />
            <Typography.Text
              style={{
                fontSize: '0.9rem',
                color: '#cf1322',
                fontWeight: 'bold',
              }}
            >
              {getPageCountInfo()?.message}
            </Typography.Text>
          </Flex>
        )}
        {/* Quality warnings badge */}
        {shouldShowQualityLabels &&
          (getSelectedProduct() as any)?.sidesWithWarnings &&
          (getSelectedProduct() as any)?.sidesWithWarnings.length > 0 && (
            <Flex
              justify="center"
              align="center"
              style={{
                backgroundColor: '#fff2f0',
                borderRadius: '6px',
                padding: '8px 12px',
                border: '1px solid #ffccc7',
                margin: '8px 0',
              }}
            >
              <IoAlertCircleOutline
                style={{
                  fontSize: '1rem',
                  marginRight: '6px',
                  color: '#cf1322',
                }}
              />
              <Typography.Text
                style={{
                  fontSize: '0.9rem',
                  color: '#cf1322',
                  fontWeight: 'bold',
                }}
              >
                {t('PagesWithWarnings', {
                  count: (getSelectedProduct() as any)?.sidesWithWarnings?.length || 0,
                })}
              </Typography.Text>
            </Flex>
          )}
        <Row
          gutter={[2, 2]}
          style={{
            marginTop: '6px',
            overflowY: 'auto',
            maxHeight: 'calc(100vh - 420px)',
            padding: '0 2px',
          }}
          ref={rowRef}
        >
          {children}
        </Row>
        {showInstructions && <Instructions />}
      </SidebarCard>
    </>
  );
};

export default Assets;
